// "Volumen por fecha de arribo (o de zarpe)": volumen de la temporada por fecha,
// a partir de get_industry_volume_arrivals. La fecha es la de la base elegida.

export type Via = 'M' | 'A' | 'T';
// [fecha, nave, puerto, país, variedad, exportadora, kilos, kilos tuyos, vía, arribado]
export type ArrivalRow = [string, string, string, string, string, string, number, number, Via, boolean];
// [fecha, nave, puerto, país, vía, exportadoras, kilos, kilos tuyos, arribado,
//  variedades a bordo, arribo, zarpe]
export type VoyageRow = [
  string, string, string, string, Via, number, number, number, boolean, string[], string | null, string | null,
];

export type ArrivalsData = {
  season: string;
  basis?: 'etd' | 'eta';
  data_date: string | null;
  top_exporters: { key: string; name: string }[];
  // Opciones de los filtros de variedad y exportadora (sin filtrar).
  variety_options?: [string, number][];
  exporter_options?: [string, string, number][];
  rows: ArrivalRow[];
  voyages: VoyageRow[];
};

export type ColorBy = 'country' | 'variety' | 'exporter' | 'via';
export type Granularity = 'day' | 'week' | 'month';
export type Period = 'all' | 'arrived' | 'pending';

export type ArrivalFilters = {
  // Destinos elegidos como "país|puerto"; vacío: todos.
  destinations: string[];
  // Vías (M marítimo, A aéreo, T terrestre); vacío: todas.
  vias: Via[];
  period: Period;
};

export const destinationKey = (country: string, port: string) => `${country}|${port}`;

const OTHERS = 'OTROS';
// Colores por destino: los 6 países más grandes; el resto, "OTROS".
const TOP_COUNTRIES = 6;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const dayMs = (day: string) => Date.parse(`${day.slice(0, 10)}T00:00:00Z`);
// Lunes (ISO) de la semana de un día.
export const mondayOf = (day: string) => {
  const ms = dayMs(day);
  return isoDay(ms - ((new Date(ms).getUTCDay() + 6) % 7) * MILLISECONDS_PER_DAY);
};
export const sundayOf = (monday: string) => isoDay(dayMs(monday) + 6 * MILLISECONDS_PER_DAY);
export const isoWeek = (day: string) => {
  const d = new Date(dayMs(day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart) / MILLISECONDS_PER_DAY + 1) / 7);
};

const matchesRow = (r: ArrivalRow, f: ArrivalFilters) =>
  (!f.destinations.length || f.destinations.includes(destinationKey(r[3], r[2]))) &&
  (!f.vias.length || f.vias.includes(r[8])) &&
  (f.period === 'all' || (f.period === 'arrived' ? r[9] : !r[9]));

const matchesVoyage = (v: VoyageRow, f: ArrivalFilters) =>
  (!f.destinations.length || f.destinations.includes(destinationKey(v[3], v[2]))) &&
  (!f.vias.length || f.vias.includes(v[4])) &&
  (f.period === 'all' || (f.period === 'arrived' ? v[8] : !v[8]));

// Día, lunes de la semana o primer día del mes.
export const bucketOf = (date: string, granularity: Granularity) =>
  granularity === 'week' ? mondayOf(date) : granularity === 'month' ? `${date.slice(0, 7)}-01` : date.slice(0, 10);

// Último día de una barra (el mismo día, el domingo o el fin de mes).
export const bucketEnd = (bucket: string, granularity: Granularity) => {
  if (granularity === 'week') return sundayOf(bucket);
  if (granularity === 'month') {
    const [y, m] = bucket.split('-').map(Number);
    return isoDay(Date.UTC(y, m, 0));
  }
  return bucket;
};

// Países con sus puertos y volumen, para el filtro Destino (del más grande al menor).
export const destinationGroups = (data: ArrivalsData) => {
  const countries = new Map<string, { kilos: number; ports: Map<string, number> }>();
  for (const [, , port, country, , , kilos] of data.rows) {
    const c = countries.get(country) ?? { kilos: 0, ports: new Map<string, number>() };
    c.kilos += kilos;
    c.ports.set(port, (c.ports.get(port) ?? 0) + kilos);
    countries.set(country, c);
  }
  return Array.from(countries, ([country, c]) => ({
    country,
    kilos: c.kilos,
    ports: Array.from(c.ports, ([port, kilos]) => ({ port, kilos })).sort((a, b) => b.kilos - a.kilos),
  })).sort((a, b) => b.kilos - a.kilos);
};

// Vías con su volumen, para el filtro Flete.
export const viaOptions = (data: ArrivalsData) => {
  const vias = new Map<Via, number>();
  for (const r of data.rows) vias.set(r[8], (vias.get(r[8]) ?? 0) + r[6]);
  return Array.from(vias, ([via, kilos]) => ({ via, kilos })).sort((a, b) => b.kilos - a.kilos);
};

// Barras apiladas: una por día, semana o mes, partidas por el desglose elegido.
export const arrivalBars = (
  data: ArrivalsData,
  { colorBy, granularity, ...filters }: ArrivalFilters & { colorBy: ColorBy; granularity: Granularity }
) => {
  const rows = data.rows.filter((r) => matchesRow(r, filters));
  const groupOfRow = (r: ArrivalRow) =>
    colorBy === 'country' ? r[3] : colorBy === 'variety' ? r[4] : colorBy === 'exporter' ? r[5] : r[8];

  // Grupos de color: por país, los más grandes; por variedad y exportadora, los
  // que ya vienen agrupados (las principales y OTRAS); por flete, las 3 vías.
  const totals = new Map<string, number>();
  const mine = new Map<string, number>();
  rows.forEach((r) => {
    totals.set(groupOfRow(r), (totals.get(groupOfRow(r)) ?? 0) + r[6]);
    mine.set(groupOfRow(r), (mine.get(groupOfRow(r)) ?? 0) + r[7]);
  });
  let groups = Array.from(totals.keys()).sort((a, b) => {
    const isOther = (g: string) => g === 'OTRAS' || g === OTHERS;
    if (isOther(a) !== isOther(b)) return isOther(a) ? 1 : -1;
    return (totals.get(b) ?? 0) - (totals.get(a) ?? 0);
  });
  const collapse = new Set<string>();
  if (colorBy === 'country' && groups.length > TOP_COUNTRIES + 1) {
    groups.slice(TOP_COUNTRIES).forEach((g) => collapse.add(g));
    groups = [...groups.slice(0, TOP_COUNTRIES), OTHERS];
  }
  const groupOf = (r: ArrivalRow) => (collapse.has(groupOfRow(r)) ? OTHERS : groupOfRow(r));

  const buckets = Array.from(new Set(rows.map((r) => bucketOf(r[0], granularity)))).sort();
  const index = new Map(buckets.map((b, i) => [b, i]));
  const series = groups.map((name) => ({ name, data: new Array(buckets.length).fill(0) as number[] }));
  const seriesIndex = new Map(groups.map((g, i) => [g, i]));
  rows.forEach((r) => {
    series[seriesIndex.get(groupOf(r))!].data[index.get(bucketOf(r[0], granularity))!] += r[6];
  });

  const legend = groups.map((name) => {
    const members = name === OTHERS && collapse.size ? Array.from(collapse) : [name];
    const kilos = members.reduce((t, g) => t + (totals.get(g) ?? 0), 0);
    const mineKilos = members.reduce((t, g) => t + (mine.get(g) ?? 0), 0);
    return { name, kilos, mine: mineKilos };
  });
  const total = legend.reduce((t, l) => t + l.kilos, 0);
  const bucketTotals = buckets.map((_, i) => series.reduce((t, s) => t + s.data[i], 0));
  return {
    buckets,
    series,
    bucketTotals,
    // La barra más cargada (la que se muestra por defecto con la temporada cerrada).
    busiest: buckets[bucketTotals.indexOf(Math.max(0, ...bucketTotals))] ?? null,
    legend: legend.map((l) => ({ ...l, share: total ? l.kilos / total : 0 })),
    total,
  };
};

// Naves de una barra (un día, una semana o un mes), de la más cargada a la menos.
export const bucketVoyages = (
  data: ArrivalsData,
  bucket: string,
  { granularity, ...filters }: ArrivalFilters & { granularity: Granularity }
) =>
  data.voyages
    .filter((v) => bucketOf(v[0], granularity) === bucket && matchesVoyage(v, filters))
    .map(([date, ship, port, country, via, exporters, kilos, mine, arrived, varieties, eta, etd]) => ({
      date, ship, port, country, via, exporters, kilos, mine, arrived, varieties, eta, etd,
    }))
    .sort((a, b) => b.kilos - a.kilos);
